import type { HandoverDetail, HandoverPhoto } from '@acasa/shared'
import { useRef, useState } from 'react'
import { photoUrl, useDeletePhoto, useUploadPhoto } from './use-handovers'
import { frozenIn } from './versions'

/**
 * Photo capture and thumbnails for one meter, room or party.
 *
 * `capture="environment"` opens the rear camera directly on a phone while
 * staying an ordinary file picker on a desktop — which is exactly the "works in
 * both contexts" behaviour, with no branching.
 *
 * Uploads run one file at a time and report per-file failures. Someone in an
 * empty flat with one bar of signal must not lose eight photos because the
 * seventh timed out.
 *
 * While a completed protocol is being changed, photos that belong to an
 * earlier Fassung keep no delete button: they are part of a frozen PDF, and
 * the server refuses to delete them anyway. New photos can still be added and
 * removed like on a draft.
 */
export function PhotoStrip({
  protocol,
  section,
  refKey,
  readOnly,
  label = 'Foto aufnehmen',
}: {
  protocol: HandoverDetail
  section: HandoverPhoto['section']
  refKey: string | null
  readOnly: boolean
  label?: string
}) {
  const input = useRef<HTMLInputElement>(null)
  const upload = useUploadPhoto(protocol.id)
  const remove = useDeletePhoto(protocol.id)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(0)

  const photos = protocol.photos.filter(
    (photo) => photo.section === section && photo.refKey === refKey,
  )

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setFailed(null)
    setBusy(files.length)

    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync({ file, section, refKey })
      } catch (error) {
        setFailed(error instanceof Error ? error.message : 'Foto fehlgeschlagen')
      } finally {
        setBusy((count) => count - 1)
      }
    }

    if (input.current) input.current.value = ''
  }

  if (readOnly && photos.length === 0) return null

  return (
    <div className="mt-3">
      {(photos.length > 0 || busy > 0) && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {photos.map((photo) => {
            const frozen = frozenIn(protocol, photo)
            return (
              <li
                key={photo.id}
                className="group relative"
                title={frozen === null ? undefined : `gehört zu Fassung ${frozen}`}
              >
                <a
                  href={photoUrl(protocol.id, photo.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <img
                    src={photoUrl(protocol.id, photo.id)}
                    alt={photo.fileName}
                    loading="lazy"
                    className="border-line size-20 border object-cover"
                  />
                </a>
                {!readOnly && frozen === null && (
                  <button
                    type="button"
                    onClick={() => {
                      setFailed(null)
                      remove.mutate(photo.id, {
                        onError: (error) =>
                          setFailed(
                            error instanceof Error
                              ? error.message
                              : 'Foto konnte nicht gelöscht werden',
                          ),
                      })
                    }}
                    aria-label="Foto entfernen"
                    className="bg-surface border-line text-muted hover:text-danger absolute -top-1.5 -right-1.5 size-6 border text-xs leading-none shadow-sm"
                  >
                    ×
                  </button>
                )}
              </li>
            )
          })}
          {busy > 0 && (
            <li className="border-line text-faint flex size-20 items-center justify-center border border-dashed text-[10px]">
              lädt …
            </li>
          )}
        </ul>
      )}

      {!readOnly && (
        <>
          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            hidden
            onChange={(event) => void onFiles(event.target.files)}
          />
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="border-line text-muted hover:border-brand hover:text-brand border px-3 py-2 text-xs transition-colors"
          >
            {label}
          </button>
        </>
      )}

      {failed && <p className="text-danger mt-1.5 text-[11px]">{failed}</p>}
    </div>
  )
}
